import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { FaqSection, FaqItem } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { FAQSectionDialog } from '@/components/faqs/FAQSectionDialog'
import { FAQItemDialog } from '@/components/faqs/FAQItemDialog'
import { FAQSectionCard } from '@/components/faqs/FAQSectionCard'
import { useToast } from '@/hooks/use-toast'
import { Plus, HelpCircle, ChevronsUpDown, ChevronsDownUp } from 'lucide-react'

type SectionWithItems = FaqSection & { faq_items: FaqItem[] }

export function FAQsPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Dialog states
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false)
  const [editSectionId, setEditSectionId] = useState<string | null>(null)
  const [itemDialogOpen, setItemDialogOpen] = useState(false)
  const [editItemId, setEditItemId] = useState<string | null>(null)
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)

  // Delete confirmation states
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'section' | 'item'
    id: string
    name: string
  } | null>(null)

  // Expand/Collapse all sections state
  const [sectionsExpanded, setSectionsExpanded] = useState<boolean | undefined>(
    undefined
  )

  // Use impersonated account when admin is impersonating
  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch sections with nested items
  const { data: sections, isLoading } = useQuery({
    queryKey: ['faq-sections', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('faq_sections')
        .select(`
          *,
          faq_items (*)
        `)
        .eq('account_id', activeAccount.id)
        .order('display_order')
      if (error) throw error
      return data as SectionWithItems[]
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch uncategorized items (items without a section)
  const { data: uncategorizedItems } = useQuery({
    queryKey: ['faq-items-uncategorized', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('faq_items')
        .select('*')
        .eq('account_id', activeAccount.id)
        .is('section_id', null)
        .order('display_order')
      if (error) throw error
      return data as FaqItem[]
    },
    enabled: !!activeAccount?.id,
  })

  // Delete section mutation
  const deleteSectionMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('faq_sections').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-sections'] })
      queryClient.invalidateQueries({ queryKey: ['faq-items-uncategorized'] })
      toast({
        title: 'Section deleted',
        description: 'The FAQ section has been successfully deleted.',
      })
      setDeleteTarget(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Delete item mutation
  const deleteItemMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('faq_items').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-sections'] })
      queryClient.invalidateQueries({ queryKey: ['faq-items-uncategorized'] })
      toast({
        title: 'FAQ deleted',
        description: 'The FAQ has been successfully deleted.',
      })
      setDeleteTarget(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleAddItem = (sectionId: string | null) => {
    setSelectedSectionId(sectionId)
    setItemDialogOpen(true)
  }

  const handleEditItem = (itemId: string) => {
    setEditItemId(itemId)
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    if (deleteTarget.type === 'section') {
      deleteSectionMutation.mutate(deleteTarget.id)
    } else {
      deleteItemMutation.mutate(deleteTarget.id)
    }
  }

  const hasNoContent =
    (!sections || sections.length === 0) &&
    (!uncategorizedItems || uncategorizedItems.length === 0)

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">FAQs</h1>
          <p className="text-muted-foreground">
            Manage frequently asked questions for your account
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!hasNoContent && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSectionsExpanded(true)}
              >
                <ChevronsUpDown className="mr-2 h-4 w-4" />
                Expand All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSectionsExpanded(false)}
              >
                <ChevronsDownUp className="mr-2 h-4 w-4" />
                Collapse All
              </Button>
            </>
          )}
          <Button onClick={() => setSectionDialogOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Add Section
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : hasNoContent ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <HelpCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No FAQs yet</h3>
          <p className="text-muted-foreground mb-4 max-w-md">
            Get started by creating a section to organize your FAQs, then add
            questions and answers.
          </p>
          <Button onClick={() => setSectionDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create First Section
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {sections?.map((section) => (
            <FAQSectionCard
              key={section.id}
              section={section}
              onEditSection={() => setEditSectionId(section.id)}
              onDeleteSection={() =>
                setDeleteTarget({
                  type: 'section',
                  id: section.id,
                  name: section.name,
                })
              }
              onAddItem={() => handleAddItem(section.id)}
              onEditItem={handleEditItem}
              onDeleteItem={(itemId) => {
                const item = section.faq_items.find((i) => i.id === itemId)
                setDeleteTarget({
                  type: 'item',
                  id: itemId,
                  name: item?.question || 'this FAQ',
                })
              }}
              sectionExpanded={sectionsExpanded}
            />
          ))}

          {/* Uncategorized items section */}
          {uncategorizedItems && uncategorizedItems.length > 0 && (
            <FAQSectionCard
              section={{
                id: 'uncategorized',
                account_id: activeAccount?.id || '',
                name: 'Uncategorized',
                slug: 'uncategorized',
                description: 'FAQs not assigned to any section',
                display_order: 9999,
                is_active: true,
                created_at: '',
                updated_at: '',
                faq_items: uncategorizedItems,
              }}
              onEditSection={() => {
                // Can't edit the virtual uncategorized section
              }}
              onDeleteSection={() => {
                // Can't delete the virtual uncategorized section
              }}
              onAddItem={() => handleAddItem(null)}
              onEditItem={handleEditItem}
              onDeleteItem={(itemId) => {
                const item = uncategorizedItems.find((i) => i.id === itemId)
                setDeleteTarget({
                  type: 'item',
                  id: itemId,
                  name: item?.question || 'this FAQ',
                })
              }}
              sectionExpanded={sectionsExpanded}
            />
          )}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'section' ? (
                <>
                  This will permanently delete the section "{deleteTarget.name}"
                  and move all its FAQs to uncategorized. This action cannot be
                  undone.
                </>
              ) : (
                <>
                  This will permanently delete the FAQ "{deleteTarget?.name}".
                  This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Section dialogs */}
      {activeAccount && (
        <>
          <FAQSectionDialog
            sectionId={null}
            accountId={activeAccount.id}
            open={sectionDialogOpen}
            onOpenChange={setSectionDialogOpen}
          />

          <FAQSectionDialog
            sectionId={editSectionId}
            accountId={activeAccount.id}
            open={!!editSectionId}
            onOpenChange={(open) => !open && setEditSectionId(null)}
          />

          {/* Item dialogs */}
          <FAQItemDialog
            itemId={null}
            accountId={activeAccount.id}
            defaultSectionId={selectedSectionId}
            open={itemDialogOpen}
            onOpenChange={(open) => {
              setItemDialogOpen(open)
              if (!open) setSelectedSectionId(null)
            }}
          />

          <FAQItemDialog
            itemId={editItemId}
            accountId={activeAccount.id}
            open={!!editItemId}
            onOpenChange={(open) => !open && setEditItemId(null)}
          />
        </>
      )}
    </div>
  )
}
